import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Card,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit, Science } from '@mui/icons-material';
import apiClient from '../lib/axios';

interface PharmaceuticalLab {
  id: string;
  name: string;
  contactInfo: string | null;
  createdAt: string;
  updatedAt: string;
}

const NAVY = '#0D1B35';
const GREEN = '#10B981';

export const PharmaceuticalLabsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedLab, setSelectedLab] = useState<PharmaceuticalLab | null>(null);
  const [labName, setLabName] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: labs, isLoading, error: queryError } = useQuery({
    queryKey: ['pharmaceutical-labs'],
    queryFn: async () => {
      const { data } = await apiClient.get<PharmaceuticalLab[]>('/pharmaceutical-labs');
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async ({ name, contactInfo }: { name: string; contactInfo?: string }) => {
      await apiClient.post('/pharmaceutical-labs', { name, contactInfo });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmaceutical-labs'] });
      setCreateDialogOpen(false);
      setLabName('');
      setContactInfo('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create pharmaceutical lab');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, contactInfo }: { id: string; name: string; contactInfo?: string }) => {
      await apiClient.put(`/pharmaceutical-labs/${id}`, { name, contactInfo });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmaceutical-labs'] });
      setEditDialogOpen(false);
      setSelectedLab(null);
      setLabName('');
      setContactInfo('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update pharmaceutical lab');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/pharmaceutical-labs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmaceutical-labs'] });
      setDeleteDialogOpen(false);
      setSelectedLab(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete pharmaceutical lab');
    },
  });

  const handleCreateClick = () => {
    setLabName('');
    setContactInfo('');
    setError(null);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (lab: PharmaceuticalLab) => {
    setSelectedLab(lab);
    setLabName(lab.name);
    setContactInfo(lab.contactInfo || '');
    setError(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (lab: PharmaceuticalLab) => {
    setSelectedLab(lab);
    setError(null);
    setDeleteDialogOpen(true);
  };

  const handleCreateConfirm = () => {
    if (labName.trim().length >= 2) {
      createMutation.mutate({
        name: labName.trim(),
        contactInfo: contactInfo.trim() || undefined,
      });
    }
  };

  const handleEditConfirm = () => {
    if (selectedLab && labName.trim().length >= 2) {
      updateMutation.mutate({
        id: selectedLab.id,
        name: labName.trim(),
        contactInfo: contactInfo.trim() || undefined,
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedLab) {
      deleteMutation.mutate(selectedLab.id);
    }
  };

  const allLabs = labs ?? [];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(16,185,129,0.1)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(16,185,129,0.06)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                LABORATORIOS
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              Laboratorios Farmacéuticos
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              {allLabs.length} laboratorios registrados
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ bgcolor: GREEN, flexShrink: 0, mt: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#059669' } }}
          >
            {isMobile ? 'Agregar' : 'Agregar Laboratorio'}
          </Button>
        </Box>
      </Box>

      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load pharmaceutical labs. Please try again.</Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {allLabs.length > 0 ? (
            allLabs.map((lab) => (
              <Card key={lab.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: `${GREEN}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: GREEN, flexShrink: 0 }}>
                      <Science sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 15 }}>{lab.name}</Typography>
                      {lab.contactInfo && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }} noWrap>{lab.contactInfo}</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {new Date(lab.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(lab)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(lab)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Science sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>No pharmaceutical labs found. Create one to get started.</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Info</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allLabs.length > 0 ? (
                  allLabs.map((lab) => (
                    <TableRow key={lab.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight={500}>{lab.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{lab.contactInfo || '—'}</Typography>
                      </TableCell>
                      <TableCell>{new Date(lab.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(lab)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(lab)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" py={4}>
                        No pharmaceutical labs found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Pharmaceutical Lab</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the pharmaceutical lab details.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Lab Name"
            fullWidth
            value={labName}
            onChange={(e) => setLabName(e.target.value)}
            error={labName.length > 0 && labName.length < 2}
            helperText={labName.length > 0 && labName.length < 2 ? 'Name must be at least 2 characters' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Contact Info (Optional)"
            fullWidth
            value={contactInfo}
            onChange={(e) => setContactInfo(e.target.value)}
            placeholder="Email, phone, or other contact details"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateConfirm}
            variant="contained"
            disabled={labName.trim().length < 2 || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Pharmaceutical Lab</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the pharmaceutical lab details.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Lab Name"
            fullWidth
            value={labName}
            onChange={(e) => setLabName(e.target.value)}
            error={labName.length > 0 && labName.length < 2}
            helperText={labName.length > 0 && labName.length < 2 ? 'Name must be at least 2 characters' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Contact Info (Optional)"
            fullWidth
            value={contactInfo}
            onChange={(e) => setContactInfo(e.target.value)}
            placeholder="Email, phone, or other contact details"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditConfirm}
            variant="contained"
            disabled={labName.trim().length < 2 || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Pharmaceutical Lab</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedLab?.name}</strong>?
            {!error && ' This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
